/**
 * Adapt an item-list row to the search result view-model, so the item list and
 * the search use one disclosure component (filter-table-reuse 02, variant A).
 *
 * The item list is item-centric: the row opens the item, and the identity line
 * shows the item's own UUID. The placement path still comes from the first
 * visible placement (the box you would walk to); an unplaced item has none.
 */

import { pathLabelOf, type Container, type ContainerItem, type Item } from "@egalregal/shared";
import { requests } from "../../shared/api";
import type { SearchHit } from "../search/searchHits";

export function itemToSearchHit(
  item: Item,
  containers: readonly Container[],
  placements: readonly ContainerItem[],
): SearchHit {
  const byId = new Map(containers.map((container) => [container.id, container]));
  // Only placements in a readable container count: an item in a container the
  // user cannot open has no link to offer.
  const itemPlacements = placements.filter(
    (placement) => placement.itemId === item.id && byId.has(placement.containerId),
  );
  const hostId = itemPlacements[0]?.containerId;
  const host = hostId ? byId.get(hostId) : undefined;
  const path = host ? pathLabelOf(containers, host.id) : "";
  const placementList = itemPlacements
    .map((placement) => {
      const container = byId.get(placement.containerId);
      return {
        containerId: placement.containerId,
        name: container?.name ?? placement.containerId,
        path: container ? pathLabelOf(containers, container.id) : placement.containerId,
        quantity: placement.quantity,
      };
    })
    .sort((left, right) => left.path.localeCompare(right.path));

  const localPhotos = item.photos.map((photo) => ({
    key: photo.id,
    url: requests.media.itemPhotoPreviewUrl(item.id, photo.id),
    label: `${item.name} · ${photo.type}`,
  }));
  const photos =
    localPhotos.length > 0
      ? localPhotos
      : item.photo
        ? [{ key: "external", url: item.photo, label: item.name }]
        : [];

  return {
    result: {
      type: "item",
      item,
      containerId: host?.id ?? "",
      containerName: host?.name ?? "",
      path,
      score: 1,
    },
    key: `item:${item.id}`,
    name: item.name,
    trail: path ? `${path} › ${item.name}` : "",
    description: item.description,
    owner: host?.owner ?? "",
    tags: host?.tags ?? [],
    type: item.category,
    photos,
    contents: [],
    properties: item.properties,
    noPlacement: !host,
    // The item list row stays item-centric, but a single placement can offer the
    // host link and quantity; multiple placements leave the row unchanged.
    placements: placementList.map(({ containerId, name, path: placementPath }) => ({
      containerId,
      name,
      path: placementPath,
    })),
    ...(placementList.length === 1 ? { quantity: placementList[0]!.quantity } : {}),
  };
}
